/**
 * Rota raiz do SPA.
 *
 * Ela nao desenha tela: os dois layouts irmaos abaixo dela fazem isso — o publico
 * (`publico.tsx`, sem shell: entrar e senha) e o protegido (`aplicacao.tsx`, com o shell e a
 * guarda de sessao). O que mora aqui e o que vale para os dois: o contexto do roteador, a tela
 * de rota inexistente e a de falha.
 *
 * O contexto carrega o `QueryClient` porque a guarda de rota precisa consultar a sessao
 * **fora** de componente, antes de renderizar qualquer coisa — e `useQuery` nao existe la.
 */

import { type QueryClient } from '@tanstack/react-query'
import {
  type ErrorComponentProps,
  Link,
  Outlet,
  createRootRouteWithContext,
} from '@tanstack/react-router'

import { ErroDaApi, MENSAGEM_DE_ERRO_DESCONHECIDO } from '@/api/erros'
import './entrar.css'

/** O que toda rota enxerga em `context`. */
export interface ContextoDoRoteador {
  clienteDeConsultas: QueryClient
}

function Raiz() {
  return <Outlet />
}

/** Rota que nao existe: em portugues, e com a saida a um clique (NFR-8). */
function NaoEncontrada() {
  return (
    <div className="entrada">
      <div className="entrada__cartao">
        <h1 className="entrada__titulo">Página não encontrada</h1>
        <p className="entrada__texto">
          O endereço digitado não existe no CRM. Ele pode ter mudado, ou o link estar
          incompleto.
        </p>
        <Link to="/" className="entrada__link">
          Voltar ao início
        </Link>
      </div>
    </div>
  )
}

/**
 * Falha que nenhuma rota soube tratar.
 *
 * E aqui que param as recusas que **nao** sao "entre": um `500` do gateway, DNS caido, o
 * navegador offline. A guarda de `aplicacao.tsx` so redireciona no `401`, de proposito —
 * mandar quem tomou um `500` para a tela de entrada faria a senha certa falhar la e a pessoa
 * concluir que esqueceu a senha. Sem este componente, a mesma falha viraria tela em branco com
 * um aviso no console.
 *
 * A mensagem do servidor sai **verbatim**, como em toda tela: o SPA nao reescreve texto do
 * servico.
 */
function Falhou({ error, reset }: ErrorComponentProps) {
  const mensagem =
    error instanceof ErroDaApi ? error.message : MENSAGEM_DE_ERRO_DESCONHECIDO

  return (
    <div className="entrada">
      <div className="entrada__cartao">
        <h1 className="entrada__titulo">Não foi possível carregar</h1>
        <p className="entrada__erro" role="alert">
          {mensagem}
        </p>
        <p className="entrada__texto">
          Isto não é sessão expirada: suas credenciais continuam valendo. Tente de novo em
          instantes.
        </p>
        <button className="entrada__botao" type="button" onClick={reset}>
          Tentar de novo
        </button>
        <Link to="/entrar" className="entrada__link">
          Ir para a tela de entrada
        </Link>
      </div>
    </div>
  )
}

export const rotaRaiz = createRootRouteWithContext<ContextoDoRoteador>()({
  component: Raiz,
  notFoundComponent: NaoEncontrada,
  errorComponent: Falhou,
})
